import { ReviewContextFields } from '@/components/screenata/review/ReviewContextFields'
import { PolicyReviewHistory } from './PolicyReviewHistory'
import { invalidatePolicyQueries } from '@/utils/policy-cache'
import type { PolicyReviewTargetDTO } from '@deepguide-ai/types'
import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { Button } from '@deepguide-ai/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@deepguide-ai/ui/dialog'
import { authClient } from '@/lib/auth-client'
import { useUploadFile } from '@/lib/upload'

export function PolicyReviewAction({ policyDocumentId, policyTitle, reviewTarget, onViewVersion }: {
  policyDocumentId: string
  policyTitle: string
  reviewTarget: PolicyReviewTargetDTO | null
  onViewVersion: (version: number) => void
}) {
  const queryClient = useQueryClient()
  const [dialog, setDialog] = useState<'confirm' | 'history' | null>(null)
  const [notes, setNotes] = useState('')
  const [participants, setParticipants] = useState<string[]>([])
  const [attachments, setAttachments] = useState<Array<{ id: string; name: string }>>([])
  const { data: session } = authClient.useSession()
  const members = useQuery({ ...trpc.workspaceMembers.list.queryOptions(), enabled: dialog === 'confirm' })
  const { uploadFile, isUploading } = useUploadFile({
    onUploadComplete: (file) => setAttachments((current) => [...current, { id: file.id, name: file.name }]),
  })
  const requestId = useRef('')
  const targetVersion = useRef<string | null>(null)
  const targetVersionNumber = useRef<number | null>(null)
  const history = useQuery(trpc.complianceProgram.getPolicyReviewHistory.queryOptions({ policyDocumentId }))
  const record = useMutation(trpc.complianceProgram.recordPolicyReview.mutationOptions({
    onSuccess: async () => {
      toast.success('Policy review recorded')
      setDialog(null)
      queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.getPolicyReviewHistory.queryKey({ policyDocumentId }) })
      queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.getPolicyReviewStatus.queryKey() })
      await invalidatePolicyQueries(queryClient)
      queryClient.invalidateQueries({ queryKey: trpc.guidedTemplate.getStatusById.queryKey() })
    },
    onError: (error) => toast.error(error.message),
  }))
  const currentReview = history.data?.records[0]

  function openConfirmation() {
    setNotes('')
    setParticipants([])
    setAttachments([])
    requestId.current = crypto.randomUUID()
    targetVersion.current = reviewTarget?.versionId ?? null
    targetVersionNumber.current = reviewTarget?.version ?? null
    setDialog('confirm')
  }

  return <>
    {currentReview && <span className='hidden text-xs text-muted-foreground xl:inline'>
      Last review {new Date(currentReview.reviewedAt).toLocaleDateString()}
    </span>}
    <Button variant='ghost' size='sm' onClick={() => setDialog('history')} disabled={history.isLoading}>Review history</Button>
    {reviewTarget && <Button variant='outline' size='sm' onClick={openConfirmation}>Review</Button>}
    <Dialog open={dialog !== null} onOpenChange={(value) => { if (!value && !record.isPending && !isUploading) setDialog(null) }}>
      <DialogContent className='max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{dialog === 'confirm' ? 'Confirm policy review' : 'Review history'}</DialogTitle>
          <DialogDescription>{dialog === 'confirm'
            ? `I have reviewed approved v${targetVersionNumber.current} and confirm that no changes are required.`
            : 'Previous approvals and reviews of this policy.'}</DialogDescription>
        </DialogHeader>
        <div className='space-y-3 px-6'>
          {history.error && <p role='alert' className='text-sm text-destructive'>{history.error.message}</p>}
          {dialog === 'confirm' ? <div className='space-y-4'>
            <div className='space-y-1 text-sm'>
              <p className='font-medium'>{policyTitle} · Approved v{targetVersionNumber.current}</p>
              <p className='text-muted-foreground'>Reviewed by {session?.user.name || session?.user.email} (you)</p>
              {currentReview && <p className='text-muted-foreground'>Last review: {new Date(currentReview.reviewedAt).toLocaleString()}</p>}
            </div>
            <p className='text-sm text-muted-foreground'>Check that the policy reflects current practices, and that responsibilities and scope remain accurate.</p>
            <ReviewContextFields idPrefix='policy-review' notes={notes} setNotes={setNotes} participants={participants} setParticipants={setParticipants}
              attachments={attachments} removeAttachment={(id) => setAttachments((current) => current.filter((file) => file.id !== id))} uploadFile={uploadFile}
              pending={record.isPending} isUploading={isUploading} currentUserId={session?.user.id}
              members={members.data} membersLoading={members.isLoading} membersError={members.error} />
            <p className='text-sm text-muted-foreground'>
            Your identity and the confirmation time will be recorded, and the next review date will be updated. The approved policy version will not change.
            </p>
          </div> : history.data && <PolicyReviewHistory records={history.data.records} onViewVersion={(version) => { onViewVersion(version); setDialog(null) }} />}
        </div>
        <DialogFooter>
          <Button variant='outline' disabled={record.isPending || isUploading} onClick={() => setDialog(null)}>{dialog === 'confirm' ? 'Cancel' : 'Close'}</Button>
          {dialog === 'confirm' && <Button disabled={record.isPending || isUploading || !reviewTarget || reviewTarget.versionId !== targetVersion.current} onClick={() => {
            if (!targetVersion.current) return
            record.mutate({ policyDocumentId, policyDocumentVersionId: targetVersion.current, requestId: requestId.current, notes, participants, fileIds: attachments.map((file) => file.id) })
          }}>{record.isPending ? 'Recording…' : 'Confirm review'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
}
